import { Button, Modal, Select } from 'antd';
import React, { useState } from 'react';
import { SyncOutlined, ExclamationCircleFilled, CalendarOutlined, BarChartOutlined, DownloadOutlined, LockOutlined } from '@ant-design/icons';
import { useSettings } from '@/store/settingStore';
import { ThemeMode } from '#/enum';
import axios from 'axios';
import { toast } from 'sonner';

interface CancelRecurringModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

const renderModalHeader = (isDark: boolean) => (
  <div className='flex gap-4 items-center'>
    <div className='w-[50px] h-[50px] rounded-full flex items-center justify-center' style={{ backgroundColor: isDark ? "rgba(196,18,48,0.1)" : "rgba(196,18,48,0.05)" }}>
      <SyncOutlined style={{ fontSize: "24px", color: "#C41230" }} />
    </div>
    <div className='flex flex-col'>
      <h1 className={`text-[18px] font-bold ${isDark ? 'text-white' : 'text-black'}`}>Cancel Recurring Payment</h1>
      <span className={`text-[14px] font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>We're sorry to see you go!</span>
    </div>
  </div>
);

const renderModalWarning = (isDark: boolean) => (
  <div className='w-full border rounded-lg p-3 flex gap-3' style={{ borderColor: "rgba(196,18,48,0.2)", backgroundColor: isDark ? "rgba(196,18,48,0.1)" : "rgba(196,18,48,0.05)" }}>
    <ExclamationCircleFilled style={{ color: "#C41230", fontSize: "16px", marginTop: "2px" }} />
    <div className='flex flex-col gap-1'>
      <span className={`font-bold text-[13px] ${isDark ? 'text-white' : 'text-black'}`}>Are you sure you want to cancel your recurring payment?</span>
      <span className={`text-[13px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Your current plan will remain active until the end of your billing period.</span>
    </div>
  </div>
);

const renderModalFeatures = (isDark: boolean) => (
  <div className='flex flex-col gap-4 mt-2'>
    <h2 className={`font-bold text-[15px] ${isDark ? 'text-white' : 'text-black'}`}>What happens when you cancel?</h2>
    <div className='flex flex-col gap-4'>
      <div className='flex gap-4'>
        <div className='w-[35px] h-[35px] shrink-0 rounded-lg flex items-center justify-center' style={{ backgroundColor: isDark ? "rgba(196,18,48,0.1)" : "rgba(196,18,48,0.05)" }}>
          <CalendarOutlined style={{ color: "#C41230", fontSize: "16px" }} />
        </div>
        <div className='flex flex-col'>
          <span className={`font-bold text-[13px] ${isDark ? 'text-white' : 'text-black'}`}>Plan remains active</span>
          <span className={`text-[13px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You can continue using Starter plan features until <b className='text-[#C41230]'>22 Jul 2026</b> (end of current billing period).</span>
        </div>
      </div>
      <div className='flex gap-4'>
        <div className='w-[35px] h-[35px] shrink-0 rounded-lg flex items-center justify-center' style={{ backgroundColor: isDark ? "rgba(196,18,48,0.1)" : "rgba(196,18,48,0.05)" }}>
          <BarChartOutlined style={{ color: "#C41230", fontSize: "16px" }} />
        </div>
        <div className='flex flex-col'>
          <span className={`font-bold text-[13px] ${isDark ? 'text-white' : 'text-black'}`}>No more charges</span>
          <span className={`text-[13px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You won't be charged after your current billing period ends.</span>
        </div>
      </div>
      <div className='flex gap-4'>
        <div className='w-[35px] h-[35px] shrink-0 rounded-lg flex items-center justify-center' style={{ backgroundColor: isDark ? "rgba(196,18,48,0.1)" : "rgba(196,18,48,0.05)" }}>
          <DownloadOutlined style={{ color: "#C41230", fontSize: "16px" }} />
        </div>
        <div className='flex flex-col'>
          <span className={`font-bold text-[13px] ${isDark ? 'text-white' : 'text-black'}`}>Your data is safe</span>
          <span className={`text-[13px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>All your dashboards, reports and data will be securely stored in your account.</span>
        </div>
      </div>
    </div>
  </div>
);

const REASON_OPTIONS = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_features', label: 'Missing features' },
  { value: 'not_using_enough', label: 'Not using it enough' },
  { value: 'found_alternative', label: 'Found an alternative' },
  { value: 'other', label: 'Other' },
];

const CancelRecurringModal: React.FC<CancelRecurringModalProps> = ({ open, onClose }) => {
  const { themeMode } = useSettings();
  const isDark = themeMode === ThemeMode.Dark;
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const { VITE_APP_URL: URL } = import.meta.env;
  const userId = localStorage.getItem("userId");

  const handleCancelRecurring = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${URL}/api/payments/stripe/cancel-subscription`, {
        user_id: Number(userId),
        immediate: false
      });
      if (response.data?.status === "ok") {
        toast.success("Subscription cancelled successfully");
        onClose();
      } else {
        toast.error("Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error("An error occurred while canceling the subscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} centered width={550}>
      <div className='w-full flex flex-col gap-5 p-1'>
        {renderModalHeader(isDark)}
        {renderModalWarning(isDark)}
        {renderModalFeatures(isDark)}

        <div className={`w-full h-[1px] my-2 ${isDark ? 'bg-white/10' : 'bg-black/5'}`} />

        <div className='flex flex-col gap-2'>
          <span className={`font-bold text-[14px] ${isDark ? 'text-white' : 'text-black'}`}>Why are you canceling? (Optional)</span>
          <Select
            size="large"
            placeholder="Select a reason"
            value={reason}
            onChange={(val) => setReason(val)}
            options={REASON_OPTIONS}
          />
        </div>

        <div className='flex gap-4 mt-2'>
          <Button size="large" className="flex-1 font-bold" onClick={onClose}>
            Keep Subscription
          </Button>
          <Button size="large" type="primary" className="flex-1 font-bold" style={{ backgroundColor: "#C41230", borderColor: "#C41230", color: "white" }} loading={loading} onClick={handleCancelRecurring}>
            Yes, Cancel Recurring
          </Button>
        </div>

        <div className='w-full flex items-center justify-center gap-2 mt-1'>
          <LockOutlined className={`text-[14px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          <span className={`font-medium text-[12px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Your cancellation will be processed securely.</span>
        </div>
      </div>
    </Modal>
  );
};

export default CancelRecurringModal;
